import { GET_SEARCH_DATA } from '@nile/banyan/src/Graphql/Mutations/search';
import { getTranslatedText, withErrorBoundary } from '@nile/components/src';
import { SVGIcon } from '@superops/super-icons';
import { TextStyle, Dropdown } from '@superops/ui-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_RECENT_SEARCH_DATA } from '@nile/banyan/src/Graphql/Mutations/search';
import { IndicatorSeparator } from '@nile/components/src/Pebbles/select';
import { AccountClient, useNoCacheQuery } from '@nile/graphql';
import { debounce, isEmpty } from 'lodash';
import Select from 'react-select';
import { FormattedMessage, useIntl } from 'react-intl';
import nanoid from 'nanoid';

const DropdownIndicator = () => {
  return <></>;
};

const CustomPlaceholder = () => {
  return (
    <div className="flex actr g5">
      <div className="opacity-50 flex">
        <SVGIcon name="Search" />
      </div>
      <TextStyle variation="subdued">
        <FormattedMessage id="common.search.ticket" />
      </TextStyle>
    </div>
  );
};

// TODO:ASH Add lokalise keys
const TicketSearchDropdown = (props) => {
  const intl = useIntl();

  const loadingText = getTranslatedText(
    intl,
    'reports.report.filter.client.loading'
  );
  const noSearchText = getTranslatedText(intl, 'common.no.search.found');
  const startTypingText = getTranslatedText(intl, 'admin.search.suggestions');

  const [options, setOptions] = useState([]);
  const [recentSearchOptions, setRecentSearchOptions] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [defaultMessage, setDefaultMessage] = useState(startTypingText);

  const { data: recentSearchData, refetch } = useNoCacheQuery(
    GET_RECENT_SEARCH_DATA,
    {
      client: AccountClient,
      onCompleted({ getRecentSearchInfo }) {
        const recentSearches = getRecentSearchInfo?.recentHits || [];
        const searchedTickets = [];

        recentSearches.forEach((el) => {
          if (
            el?.entityType === 'Ticket' ||
            el?.entityType === 'TicketConversation'
          ) {
            const subject = el?.displayData?.subject;
            const displayId = el?.displayData?.displayId;
            const displayvalue = `#${displayId} ${subject}`;
            const entityId =
              el?.entityType === 'Ticket' ? el?.entityId : el?.parentEntityId;
            const ticketData = {
              name: displayvalue,
              label: displayvalue,
              value: displayvalue,
              displayId: displayId,
              ticketId: entityId,
              id: nanoid(),
              entityId: entityId,
              key: nanoid()
            };
            searchedTickets.push(ticketData);
          }
        });
        if (!isEmpty(searchedTickets)) {
          setRecentSearchOptions(searchedTickets);
          setOptions(searchedTickets);
          setDefaultMessage(noSearchText);
        }
        setIsLoading(false);
      },
      onError() {
        setIsLoading(false);
      }
    }
  );

  const [onSearchAPI, { loading: searchLoading, error }] = useLazyQuery(
    GET_SEARCH_DATA,
    {
      client: AccountClient,
      fetchPolicy: 'no-cache',
      onCompleted({ search }) {
        const results = search?.documents || [];
        const searchedTickets = [];
        results.forEach((el) => {
          if (
            el?.entityType === 'Ticket' ||
            el?.entityType === 'TicketConversation'
          ) {
            const subject = el?.displayData?.subject;
            const displayId = el?.displayData?.displayId;
            const displayvalue = `#${displayId} ${subject}`;
            const entityId =
              el?.entityType === 'Ticket' ? el?.entityId : el?.parentEntityId;
            const ticketData = {
              name: displayvalue,
              label: displayvalue,
              value: displayvalue,
              displayId: displayId,
              ticketId: entityId,
              id: nanoid(),
              entityId: entityId,
              key: nanoid()
            };
            searchedTickets.push(ticketData);
          }
        });
        setIsLoading(false);
        setOptions(searchedTickets);
      },
      onError() {
        setIsLoading(false);
      }
    }
  );

  const onChange = (selected) => {
    setSelectedOption(selected);
    if (props?.onChange) {
      props?.onChange(selected);
    }
  };

  const debouncedSearch = useCallback(debounce(onSearchAPI, 300), []);

  const loadOptions = (searchString) => {
    if (!searchString) {
      setIsLoading(false);
      setOptions(recentSearchOptions);
    } else {
      setIsLoading(true);
      const variables = {
        variables: {
          term: searchString,
          modules: ['TICKET'],
          pageInfo: {
            pageSize: 30
          }
        }
      };
      debouncedSearch(variables);
    }
  };

  const onMenuOpen = () => {
    if (isEmpty(options)) {
      setOptions(recentSearchOptions);
    }
  };

  return (
    <Select
      value={selectedOption}
      onChange={onChange}
      options={options}
      components={{
        IndicatorSeparator,
        DropdownIndicator,
        Placeholder: CustomPlaceholder
      }}
      classNamePrefix="value_selector"
      menuPlacement="auto"
      menuPortalTarget={document.body}
      closeMenuOnScroll={(e) => {
        return (
          e.target &&
          e.target.classList &&
          !(e.target.classList.value.indexOf('menu-list') > -1)
        );
      }}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 })
      }}
      isLoading={searchLoading || isloading}
      isSearchable
      onInputChange={loadOptions}
      openMenuOnFocus
      noOptionsMessage={() =>
        isloading || searchLoading ? loadingText : defaultMessage
      }
      onMenuOpen={onMenuOpen}
      outline
    />
  );
};

export default withErrorBoundary(TicketSearchDropdown);
